import React from 'react';
import { useNavigate } from 'react-router-dom';

const ExplicationsPage = () => {
  const navigate = useNavigate();

  const goodMechanisms = [
    {
      name: "Coping actif",
      description: "Le sujet arrive à mettre en place des efforts comportementaux actifs pour influencer la relation personne-environnement."
    },
    {
      name: "Planification",
      description: "Le sujet arrive à élaborer un plan d'action dirigé vers la modification de la situation de stress."
    },
    {
      name: "Soutien Instrumental",
      description: "Le sujet arrive à solliciter des conseils et tout aide nécessaire à la résolution de son problème."
    },
    {
      name: "Soutien Émotionnel",
      description: "Le sujet bénéficie d'un appui émotionnel (empathie/compassion) de la part de ses proches."
    },
    {
      name: "Ventilation",
      description: "Le sujet arrive à exprimer ses émotions négatives et à décrire sa situation."
    },
    {
      name: "Recadrage Positif",
      description: "Le sujet arrive à rediriger ses pensées à éviter la victimisation."
    },
    {
      name: "Acceptation",
      description: "Le sujet accepte et fait face à sa situation."
    },
    {
      name: "Humour",
      description: "Le sujet utilise son sens de l'humour pour faire face à la situation."
    },
    {
      name: "Religion",
      description: "Le sujet utilise la croyance à un être suprême pour la résolution de son problème."
    },
    {
      name: "Distraction",
      description: "Le sujet utilise plusieurs moyens de distraction pour la résolution de sa situation."
    }
  ];

  const badMechanisms = [
    {
      name: "Déni",
      description: "Le sujet ne reconnaît pas la situation qu'il traverse."
    },
    {
      name: "Blame",
      description: "Le sujet se culpabilise de la situation."
    },
    {
      name: "Utilisation de substances",
      description: "Le sujet utilise la consommation de substances illicites/toxiques (alcool, drogue, nicotine, caféine) pour s'évader de sa situation."
    },
    {
      name: "Désengagement Comportemental",
      description: "Le sujet réduit ses efforts de trouver une solution à sa situation (arrêt des activités quotidiennes, etc.)"
    }
  ];

  return (
    <div className="min-h-screen py-8 px-4 bg-gradient-to-br from-slate-50 to-slate-100" data-testid="explications-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 mb-2">Explications des Mécanismes d'Adaptation</h1>
              <p className="text-slate-600">
                Comprenez les différentes stratégies de coping face au stress
              </p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="bg-slate-600 hover:bg-slate-700 text-white font-semibold py-3 px-6 rounded-lg transition flex items-center gap-2"
              data-testid="back-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Retour
            </button>
          </div>
        </div>

        {/* Good Mechanisms */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-lg p-8 mb-6 border-2 border-green-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-green-900">Bonne Adaptation</h2>
              <p className="text-green-700">Mécanismes adaptatifs qui favorisent la résolution et le bien-être</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {goodMechanisms.map((mechanism, idx) => (
              <div 
                key={idx} 
                className="bg-white rounded-lg p-5 border-2 border-green-200 hover:shadow-md transition"
                data-testid={`good-mechanism-${idx}`}
              >
                <h3 className="text-lg font-bold text-green-800 mb-2 flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  {mechanism.name}
                </h3>
                <p className="text-slate-700 text-sm leading-relaxed">
                  {mechanism.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bad Mechanisms */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl shadow-lg p-8 border-2 border-red-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-red-900">Mauvaise Adaptation</h2>
              <p className="text-red-700">Mécanismes maladaptatifs qui peuvent nuire à la résolution et au bien-être</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {badMechanisms.map((mechanism, idx) => (
              <div 
                key={idx} 
                className="bg-white rounded-lg p-5 border-2 border-red-200 hover:shadow-md transition"
                data-testid={`bad-mechanism-${idx}`}
              >
                <h3 className="text-lg font-bold text-red-800 mb-2 flex items-center gap-2">
                  <span className="text-red-600">⚠</span>
                  {mechanism.name}
                </h3>
                <p className="text-slate-700 text-sm leading-relaxed">
                  {mechanism.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mt-6">
          <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Note importante
          </h3>
          <p className="text-blue-800 text-sm mb-4">
            Ces mécanismes d'adaptation représentent différentes stratégies que les individus utilisent face au stress. 
            Les mécanismes de "bonne adaptation" sont généralement plus efficaces pour la résolution de problèmes et le bien-être psychologique, 
            tandis que les mécanismes de "mauvaise adaptation" peuvent offrir un soulagement temporaire mais tendent à créer des problèmes supplémentaires à long terme.
          </p>
          <div className="border-t border-blue-300 pt-4 mt-4">
            <p className="text-blue-900 text-sm font-semibold">
              Brief COPE - Inventé par Charles S. Carver et Michael F. Scheier
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExplicationsPage;
